import * as THREE from 'three/webgpu';
import { SceneAuxiliaryObjectRegistry } from '../scene-object';
import { Subject, type Observable } from 'rxjs';
import { markCadObjectAsRuntimeDerived } from '../document/CadObjectPersistence';
import { computeDirectionArrowWorldSizeFromPixels } from '../manager/directionArrow/computeDirectionArrowWorldSizeFromPixels';
import { ensureRaycastAccelerationForObjectTree } from '../raycast-acceleration';
import { ViewportCursorStyle } from '../viewer/moudles/ViewportCursorManager';
import type {
    SectionViewPlaneIndex,
    SectionViewRotationAxis,
    SectionViewRotationHandleDragEvent,
    SectionViewRotationHandleDragPhase,
    SectionViewRotationHandleRuntimeHost,
    SectionViewRotationHandleSetInput,
} from '../../types/section-view/SectionViewTypes';

const SECTION_PLANE_INDEXES = [0, 1, 2] as const;
const ROTATION_AXES = ['x', 'y'] as const;
const DRAG_PHASES = ['start', 'change', 'end'] as const;
const INITIAL_RING_RADIUS_PIXELS = 48;
const RING_TUBE_RADIUS_RATIO = 0.045;
const HEAD_RADIUS_RATIO = 0.085;
const HEAD_LENGTH_RATIO = 0.2;
const MINIMUM_AXIS_LENGTH_SQUARED = 1e-12;
const RADIANS_TO_DEGREES = 180 / Math.PI;
const FULL_ROTATION_RADIANS = Math.PI * 2;
const DEFAULT_HANDLE_COLOR = 0xf0a020;
const ACTIVE_HANDLE_COLOR = 0x2080f0;
const HANDLE_RENDER_ORDER = 15000;

/**
 * SOLIDWORKS 风格剖面双旋转手柄管理器。
 *
 * 职责与生命周期：
 * 1. 为三个固定剖面槽位各维护 X/Y 两个 180°旋转半环；
 * 2. 在捕获阶段独占命中手柄的鼠标序列，避免相机和框选同时响应；
 * 3. 发布连续角度，不自行持有剖面业务状态；
 * 4. 几何体和材质只在构造时创建并由六个半环共享，`dispose()` 对称释放。
 */
export class SectionViewRotationHandleManager extends THREE.Group {
    /** 跳过普通选择链的运行时标记。 */
    public readonly skipSelection = true;

    /** Viewer 最小运行时端口。 */
    private readonly host: SectionViewRotationHandleRuntimeHost;
    /** 六个半环共享的圆环几何。 */
    private readonly ringGeometry: THREE.TorusGeometry;
    /** 六个半环共享的箭头几何。 */
    private readonly headGeometry: THREE.ConeGeometry;
    /** 默认橙色共享材质。 */
    private readonly defaultMaterial: THREE.MeshBasicMaterial;
    /** 悬停与拖动蓝色共享材质。 */
    private readonly activeMaterial: THREE.MeshBasicMaterial;
    /** 固定三个槽位根节点。 */
    private readonly slotRoots: THREE.Group[] = [];
    /** 当前真实可见的槽位根节点；只在 set/clear 边界增量维护。 */
    private readonly visibleSlotRoots: THREE.Group[] = [];
    /** 固定六个轴向组，平面索引乘二后依次为 X、Y。 */
    private readonly axisGroups: THREE.Group[] = [];
    /** 固定六个半圆网格。 */
    private readonly ringMeshes: THREE.Mesh[] = [];
    /** 固定六个箭头网格。 */
    private readonly headMeshes: THREE.Mesh[] = [];
    /** 三个槽位的中心世界坐标缓存。 */
    private readonly anchorBuffers = SECTION_PLANE_INDEXES.map(() => new THREE.Vector3());
    /** 三个槽位的最终显示 X 轴缓存。 */
    private readonly displayXAxisBuffers = SECTION_PLANE_INDEXES.map(() => new THREE.Vector3(1, 0, 0));
    /** 三个槽位的最终显示 Y 轴缓存。 */
    private readonly displayYAxisBuffers = SECTION_PLANE_INDEXES.map(() => new THREE.Vector3(0, 1, 0));
    /** 三个槽位的最终显示法向缓存。 */
    private readonly displayNormalBuffers = SECTION_PLANE_INDEXES.map(() => new THREE.Vector3(0, 0, 1));
    /** 三个槽位的 X 属性角求值轴缓存。 */
    private readonly rotationXAxisBuffers = SECTION_PLANE_INDEXES.map(() => new THREE.Vector3(1, 0, 0));
    /** 三个槽位的 Y 属性角求值轴缓存。 */
    private readonly rotationYAxisBuffers = SECTION_PLANE_INDEXES.map(() => new THREE.Vector3(0, 1, 0));
    /** 三个槽位当前 X 角度。 */
    private readonly rotationXDegrees = [0, 0, 0];
    /** 三个槽位当前 Y 角度。 */
    private readonly rotationYDegrees = [0, 0, 0];
    /** 拾取对象到剖面槽位的弱映射，不污染 Object3D.userData。 */
    private readonly objectSectionIndex = new WeakMap<THREE.Object3D, SectionViewPlaneIndex>();
    /** 拾取对象到旋转轴的弱映射，不污染 Object3D.userData。 */
    private readonly objectRotationAxis = new WeakMap<THREE.Object3D, SectionViewRotationAxis>();
    /** 旋转拖拽事件流。 */
    private readonly rotationDragSubject = new Subject<SectionViewRotationHandleDragEvent>();
    /** 阶段、槽位和轴固定组合的事件缓存，鼠标移动热路径不创建事件对象。 */
    private readonly dragEventBuffers = SECTION_PLANE_INDEXES.flatMap((sectionIndex) =>
        ROTATION_AXES.flatMap((axis) => DRAG_PHASES.map((phase) => ({
            phase,
            sectionIndex,
            axis,
            angleDegrees: 0,
        }))));
    /** 复用的射线检测器。 */
    private readonly raycaster = new THREE.Raycaster();
    /** 复用的鼠标 NDC。 */
    private readonly pointerNdc = new THREE.Vector2();
    /** 复用的命中结果数组。 */
    private readonly intersectionBuffer: THREE.Intersection[] = [];
    /** 复用的姿态矩阵。 */
    private readonly orientationMatrix = new THREE.Matrix4();
    /** 复用的旋转轴。 */
    private readonly orientationAxis = new THREE.Vector3();
    /** 复用的半环径向基准。 */
    private readonly orientationRadial = new THREE.Vector3();
    /** 复用的半环第二径向轴。 */
    private readonly orientationTangent = new THREE.Vector3();
    /** 拖拽计算平面。 */
    private readonly dragPlane = new THREE.Plane();
    /** 拖拽平面交点。 */
    private readonly dragPoint = new THREE.Vector3();
    /** 拖拽起始单位方向。 */
    private readonly dragStartDirection = new THREE.Vector3();
    /** 拖拽当前单位方向。 */
    private readonly dragCurrentDirection = new THREE.Vector3();
    /** 拖拽旋转轴。 */
    private readonly dragAxis = new THREE.Vector3();
    /** 拖拽中心。 */
    private readonly dragAnchor = new THREE.Vector3();
    /** 有符号角计算的叉乘缓存。 */
    private readonly signedAngleCross = new THREE.Vector3();
    /** 最近一次解析命中的世界点。 */
    private readonly resolvedHitPoint = new THREE.Vector3();
    /** 当前绑定的 DOM 容器。 */
    private boundContainer: HTMLElement | null = null;
    /** 当前由旋转手柄压入统一光标栈的样式。 */
    private currentCursor: ViewportCursorStyle | null = null;
    /** 当前悬停的扁平手柄索引。 */
    private hoveredHandleIndex = -1;
    /** 最近一次解析命中的槽位。 */
    private resolvedSectionIndex: SectionViewPlaneIndex = 0;
    /** 最近一次解析命中的轴。 */
    private resolvedRotationAxis: SectionViewRotationAxis = 'x';
    /** 当前拖拽槽位；负数表示未拖拽。 */
    private draggingSectionIndex = -1;
    /** 当前拖拽轴。 */
    private draggingRotationAxis: SectionViewRotationAxis = 'x';
    /** 拖拽开始时的属性角度。 */
    private dragStartAngleDegrees = 0;
    /** 上一帧相对起点的原始有符号角。 */
    private previousRawAngleRadians = 0;
    /** 已跨越正负 180°边界的累计角度。 */
    private accumulatedAngleRadians = 0;
    /** 最近发布的连续角度。 */
    private latestAngleDegrees = 0;
    /** 下一次 click 是否属于手柄交互并应被消费。 */
    private shouldSuppressNextClick = false;
    /** 管理器是否已经释放。 */
    private isDisposed = false;

    /**
     * 创建并挂载剖面旋转手柄管理器。
     *
     * @param host Viewer 最小运行时端口。
     */
    public constructor(host: SectionViewRotationHandleRuntimeHost) {
        super();
        this.host = host;
        this.name = '__section_view_rotation_handle_manager__';
        // 手柄继续由本管理器拾取，只从组件身份与变换解析中排除。
        SceneAuxiliaryObjectRegistry.markRoot(this);
        markCadObjectAsRuntimeDerived(this);
        this.ringGeometry = new THREE.TorusGeometry(
            1,
            RING_TUBE_RADIUS_RATIO,
            10,
            64,
            Math.PI,
        );
        this.headGeometry = new THREE.ConeGeometry(
            HEAD_RADIUS_RATIO,
            HEAD_LENGTH_RATIO,
            18,
        );
        this.defaultMaterial = this.createHandleMaterial(DEFAULT_HANDLE_COLOR);
        this.activeMaterial = this.createHandleMaterial(ACTIVE_HANDLE_COLOR);
        this.createFixedHandles();
        /** 共享圆环与箭头几何只构建一次 BVH；活动剖面编辑时不再逐三角遍历高细分 Torus。 */
        ensureRaycastAccelerationForObjectTree(this);
        this.host.scene.add(this);
    }

    /**
     * 把新编辑会话的初始环可读尺寸标定为世界半径。
     *
     * 该方法只在会话开始时调用一次；返回值由 Controller 冻结，
     * 相机后续的 zoom、resize 或投影切换不再触发尺寸换算。
     *
     * @param anchorWorld 手柄世界中心。
     * @returns 有限正数的初始世界半径。
     * @throws 相机、视口或中心尚未就绪时抛出错误。
     */
    public resolveInitialWorldRadius(anchorWorld: THREE.Vector3): number {
        this.assertNotDisposed();
        const camera = this.host.getCamera();
        const container = this.host.getContainer();
        const viewportHeight = container?.clientHeight ?? 0;
        if (!camera || !container || !Number.isFinite(viewportHeight) || viewportHeight <= 0) {
            throw new Error('剖面手柄初始标定需要已就绪的相机和可见视口');
        }
        if (!Number.isFinite(anchorWorld.x)
            || !Number.isFinite(anchorWorld.y)
            || !Number.isFinite(anchorWorld.z)) {
            throw new Error('剖面手柄初始标定中心必须是有限世界坐标');
        }
        const radiusWorld = computeDirectionArrowWorldSizeFromPixels(
            camera,
            container,
            anchorWorld,
            INITIAL_RING_RADIUS_PIXELS,
        );
        if (!Number.isFinite(radiusWorld) || radiusWorld <= 0) {
            throw new Error('剖面手柄初始世界半径换算失败');
        }
        return radiusWorld;
    }

    /**
     * 创建或增量更新指定槽位的双半圆旋转手柄。
     *
     * @param sectionIndex 零基剖面槽位。
     * @param input 最终显示坐标帧、属性角求值轴与当前角度。
     * @returns 无返回值。
     */
    public setHandle(
        sectionIndex: SectionViewPlaneIndex,
        input: SectionViewRotationHandleSetInput,
    ): void {
        this.assertNotDisposed();
        const index = this.requireSectionIndex(sectionIndex);
        if (!Number.isFinite(input.rotationXDegrees) || !Number.isFinite(input.rotationYDegrees)) {
            throw new Error('剖面旋转手柄角度必须是有限数字');
        }
        if (!Number.isFinite(input.radiusWorld) || input.radiusWorld <= 0) {
            throw new Error('剖面旋转手柄世界半径必须是有限正数');
        }
        if (input.displayXAxisWorld.lengthSq() <= MINIMUM_AXIS_LENGTH_SQUARED
            || input.displayYAxisWorld.lengthSq() <= MINIMUM_AXIS_LENGTH_SQUARED
            || input.displayNormalWorld.lengthSq() <= MINIMUM_AXIS_LENGTH_SQUARED
            || input.rotationXAxisWorld.lengthSq() <= MINIMUM_AXIS_LENGTH_SQUARED
            || input.rotationYAxisWorld.lengthSq() <= MINIMUM_AXIS_LENGTH_SQUARED) {
            throw new Error('剖面旋转手柄显示坐标帧与求值轴不能为零向量');
        }

        this.anchorBuffers[index].copy(input.anchorWorld);
        this.displayNormalBuffers[index].copy(input.displayNormalWorld).normalize();
        this.displayXAxisBuffers[index].copy(input.displayXAxisWorld)
            .addScaledVector(
                this.displayNormalBuffers[index],
                -input.displayXAxisWorld.dot(this.displayNormalBuffers[index]),
            );
        if (this.displayXAxisBuffers[index].lengthSq() <= MINIMUM_AXIS_LENGTH_SQUARED) {
            throw new Error('剖面旋转手柄显示 X 轴不能与法向平行');
        }
        this.displayXAxisBuffers[index].normalize();
        this.displayYAxisBuffers[index].copy(input.displayYAxisWorld)
            .addScaledVector(
                this.displayNormalBuffers[index],
                -input.displayYAxisWorld.dot(this.displayNormalBuffers[index]),
            )
            .addScaledVector(
                this.displayXAxisBuffers[index],
                -input.displayYAxisWorld.dot(this.displayXAxisBuffers[index]),
            );
        if (this.displayYAxisBuffers[index].lengthSq() <= MINIMUM_AXIS_LENGTH_SQUARED) {
            throw new Error('剖面旋转手柄显示坐标帧必须正交');
        }
        this.displayYAxisBuffers[index].normalize();

        // 显示环使用最终剖面坐标帧，但拖拽角度仍必须沿属性栏的顺序求值轴计算。
        this.rotationXAxisBuffers[index].copy(input.rotationXAxisWorld).normalize();
        this.rotationYAxisBuffers[index].copy(input.rotationYAxisWorld)
            .addScaledVector(
                this.rotationXAxisBuffers[index],
                -input.rotationYAxisWorld.dot(this.rotationXAxisBuffers[index]),
            );
        if (this.rotationYAxisBuffers[index].lengthSq() <= MINIMUM_AXIS_LENGTH_SQUARED) {
            throw new Error('剖面旋转手柄 X/Y 求值轴不能平行');
        }
        this.rotationYAxisBuffers[index].normalize();
        this.rotationXDegrees[index] = input.rotationXDegrees;
        this.rotationYDegrees[index] = input.rotationYDegrees;
        const slotRoot = this.slotRoots[index];
        if (!slotRoot.visible) {
            slotRoot.visible = true;
            /** 保持原固定槽位顺序，重叠手柄同距离时不因启用先后改变 tie-break。 */
            const insertionIndex = this.visibleSlotRoots.findIndex((visibleRoot) => (
                this.slotRoots.indexOf(visibleRoot) > index
            ));
            if (insertionIndex < 0) {
                this.visibleSlotRoots.push(slotRoot);
            } else {
                this.visibleSlotRoots.splice(insertionIndex, 0, slotRoot);
            }
        }
        slotRoot.scale.setScalar(input.radiusWorld);
        // 求角基准已在按下时复制到独立缓存；可视双环必须与剖面和平移箭头实时使用最新显示帧。
        this.updateSlotTransform(index);
        this.bindContainerIfAvailable();
        // 显示帧必须当场写回；画面与剖面控制器的同帧更新合并提交。
        this.host.requestRender();
    }

    /**
     * 隐藏指定槽位手柄，但保留共享 GPU 资源供再次编辑复用。
     *
     * @param sectionIndex 零基剖面槽位。
     * @returns 无返回值。
     */
    public clearHandle(sectionIndex: SectionViewPlaneIndex): void {
        const index = this.requireSectionIndex(sectionIndex);
        if (!this.slotRoots[index].visible) {
            return;
        }
        this.slotRoots[index].visible = false;
        const visibleIndex = this.visibleSlotRoots.indexOf(this.slotRoots[index]);
        if (visibleIndex >= 0) {
            this.visibleSlotRoots.splice(visibleIndex, 1);
        }
        const wasHovered = Math.floor(this.hoveredHandleIndex / 2) === index;
        if (wasHovered) {
            this.setHoveredHandle(-1);
        }
        if (this.draggingSectionIndex === index) {
            this.finishDrag(false);
        }
        if (wasHovered) {
            this.setCursor(null);
        }
        /** 最后一个手柄隐藏后解除捕获监听，普通 Hover 不再进入剖面手柄调用栈。 */
        if (this.visibleSlotRoots.length === 0) {
            this.unbindContainer();
        }
        this.host.requestRender();
    }

    /**
     * 订阅双旋转手柄拖拽事件。
     *
     * @returns 同步发布 start/change/end 的只读事件流。
     */
    public observeRotationDrag(): Observable<SectionViewRotationHandleDragEvent> {
        return this.rotationDragSubject.asObservable();
    }

    /**
     * 释放 DOM 监听、事件流、场景节点与全部共享 GPU 资源。
     *
     * @returns 无返回值；重复调用安全忽略。
     */
    public dispose(): void {
        if (this.isDisposed) {
            return;
        }
        this.finishDrag(false);
        this.unbindContainer();
        this.rotationDragSubject.complete();
        this.removeFromParent();
        this.clear();
        this.ringGeometry.dispose();
        this.headGeometry.dispose();
        this.defaultMaterial.dispose();
        this.activeMaterial.dispose();
        this.isDisposed = true;
    }

    /**
     * 创建固定三个槽位、每槽两个轴向半环的场景对象。
     *
     * @returns 无返回值。
     */
    private createFixedHandles(): void {
        for (const sectionIndex of SECTION_PLANE_INDEXES) {
            const slotRoot = new THREE.Group();
            slotRoot.name = `__section_view_rotation_handle_${sectionIndex}__`;
            slotRoot.visible = false;
            this.slotRoots.push(slotRoot);
            this.add(slotRoot);

            for (const axis of ROTATION_AXES) {
                const handleIndex = this.resolveHandleIndex(sectionIndex, axis);
                const axisGroup = new THREE.Group();
                axisGroup.name = `__section_view_rotation_axis_${sectionIndex}_${axis}__`;
                const ringMesh = new THREE.Mesh(this.ringGeometry, this.defaultMaterial);
                ringMesh.name = `__section_view_rotation_ring_${sectionIndex}_${axis}__`;
                ringMesh.renderOrder = HANDLE_RENDER_ORDER + handleIndex;
                ringMesh.frustumCulled = false;
                const headMesh = new THREE.Mesh(this.headGeometry, this.defaultMaterial);
                headMesh.name = `__section_view_rotation_head_${sectionIndex}_${axis}__`;
                headMesh.position.set(-1, -HEAD_LENGTH_RATIO / 2, 0);
                headMesh.rotation.z = Math.PI;
                headMesh.renderOrder = HANDLE_RENDER_ORDER + handleIndex;
                headMesh.frustumCulled = false;
                axisGroup.add(ringMesh, headMesh);
                slotRoot.add(axisGroup);
                this.axisGroups[handleIndex] = axisGroup;
                this.ringMeshes[handleIndex] = ringMesh;
                this.headMeshes[handleIndex] = headMesh;
                this.objectSectionIndex.set(ringMesh, sectionIndex);
                this.objectSectionIndex.set(headMesh, sectionIndex);
                this.objectRotationAxis.set(ringMesh, axis);
                this.objectRotationAxis.set(headMesh, axis);
            }
        }
    }

    /**
     * 创建始终清晰可见的共享基础材质。
     *
     * @param color 颜色十六进制值。
     * @returns 新材质，生命周期由当前管理器持有。
     */
    private createHandleMaterial(color: number): THREE.MeshBasicMaterial {
        return new THREE.MeshBasicMaterial({
            color,
            depthTest: false,
            depthWrite: false,
            toneMapped: false,
        });
    }

    /**
     * 更新指定槽位的位置和最终显示坐标帧。
     *
     * @param sectionIndex 零基剖面槽位。
     * @returns 无返回值。
     */
    private updateSlotTransform(sectionIndex: SectionViewPlaneIndex): void {
        const slotRoot = this.slotRoots[sectionIndex];
        slotRoot.position.copy(this.anchorBuffers[sectionIndex]);
        this.orientAxisGroup(
            this.axisGroups[this.resolveHandleIndex(sectionIndex, 'x')],
            this.displayXAxisBuffers[sectionIndex],
            this.displayNormalBuffers[sectionIndex],
        );
        this.orientAxisGroup(
            this.axisGroups[this.resolveHandleIndex(sectionIndex, 'y')],
            this.displayYAxisBuffers[sectionIndex],
            this.displayNormalBuffers[sectionIndex],
        );
        slotRoot.updateMatrixWorld(true);
    }

    /**
     * 让半环局部 Z 轴对齐显示轴，并使半环可见中点始终指向平移箭头法向。
     *
     * @param axisGroup 待更新的半环组。
     * @param axisWorld 半环显示轴的世界方向。
     * @param forwardWorld 与平移箭头一致的世界前向。
     * @returns 无返回值。
     */
    private orientAxisGroup(
        axisGroup: THREE.Group,
        axisWorld: THREE.Vector3,
        forwardWorld: THREE.Vector3,
    ): void {
        this.orientationAxis.copy(axisWorld).normalize();
        // TorusGeometry 的 180° 中点位于局部 +Y；以 forward×axis 为径向后，
        // 局部 +Y = axis×radial = forward，两个半环因而共用平移轴上的同一前向交点。
        this.orientationRadial.crossVectors(forwardWorld, this.orientationAxis);
        if (this.orientationRadial.lengthSq() <= MINIMUM_AXIS_LENGTH_SQUARED) {
            throw new Error('剖面旋转手柄显示轴不能与法向平行');
        }
        this.orientationRadial.normalize();
        this.orientationTangent.crossVectors(this.orientationAxis, this.orientationRadial).normalize();
        this.orientationMatrix.makeBasis(
            this.orientationRadial,
            this.orientationTangent,
            this.orientationAxis,
        );
        axisGroup.quaternion.setFromRotationMatrix(this.orientationMatrix);
    }

    /**
     * 处理左键按下并建立旋转拖拽会话。
     *
     * @param event 鼠标事件。
     * @returns 无返回值。
     */
    private readonly handleMouseDown = (event: MouseEvent): void => {
        if (event.button !== 0 || !this.resolveHandleHit(event)) {
            return;
        }
        const sectionIndex = this.resolvedSectionIndex;
        const axis = this.resolvedRotationAxis;
        this.draggingSectionIndex = sectionIndex;
        this.draggingRotationAxis = axis;
        this.dragAxis.copy(axis === 'x'
            ? this.rotationXAxisBuffers[sectionIndex]
            : this.rotationYAxisBuffers[sectionIndex]);
        this.dragAnchor.copy(this.anchorBuffers[sectionIndex]);
        this.dragPlane.setFromNormalAndCoplanarPoint(this.dragAxis, this.dragAnchor);
        this.dragStartDirection.copy(this.resolvedHitPoint).sub(this.dragAnchor);
        this.dragStartDirection.addScaledVector(
            this.dragAxis,
            -this.dragStartDirection.dot(this.dragAxis),
        );
        if (this.dragStartDirection.lengthSq() <= MINIMUM_AXIS_LENGTH_SQUARED) {
            this.draggingSectionIndex = -1;
            return;
        }
        this.dragStartDirection.normalize();
        this.dragStartAngleDegrees = axis === 'x'
            ? this.rotationXDegrees[sectionIndex]
            : this.rotationYDegrees[sectionIndex];
        this.latestAngleDegrees = this.dragStartAngleDegrees;
        this.previousRawAngleRadians = 0;
        this.accumulatedAngleRadians = 0;
        this.shouldSuppressNextClick = true;
        this.setHoveredHandle(this.resolveHandleIndex(sectionIndex, axis));
        this.setCursor(ViewportCursorStyle.Grabbing);
        this.emitDragEvent('start', this.latestAngleDegrees);
        this.consumeEvent(event);
    };

    /**
     * 处理悬停与连续旋转拖拽。
     *
     * @param event 鼠标事件。
     * @returns 无返回值。
     */
    private readonly handleMouseMove = (event: MouseEvent): void => {
        if (this.draggingSectionIndex < 0) {
            const hasHit = this.resolveHandleHit(event);
            this.setHoveredHandle(hasHit
                ? this.resolveHandleIndex(this.resolvedSectionIndex, this.resolvedRotationAxis)
                : -1);
            this.setCursor(hasHit ? ViewportCursorStyle.Grab : null);
            if (hasHit) {
                this.consumeEvent(event);
            }
            return;
        }

        if (!this.updateRayFromEvent(event)) {
            this.consumeEvent(event);
            return;
        }
        const hitPoint = this.raycaster.ray.intersectPlane(this.dragPlane, this.dragPoint);
        if (!hitPoint) {
            this.consumeEvent(event);
            return;
        }
        this.dragCurrentDirection.copy(hitPoint).sub(this.dragAnchor);
        this.dragCurrentDirection.addScaledVector(
            this.dragAxis,
            -this.dragCurrentDirection.dot(this.dragAxis),
        );
        if (this.dragCurrentDirection.lengthSq() <= MINIMUM_AXIS_LENGTH_SQUARED) {
            this.consumeEvent(event);
            return;
        }
        this.dragCurrentDirection.normalize();
        const rawAngleRadians = Math.atan2(
            this.dragAxis.dot(this.signedAngleCross.crossVectors(
                this.dragStartDirection,
                this.dragCurrentDirection,
            )),
            this.dragStartDirection.dot(this.dragCurrentDirection),
        );
        let frameDeltaRadians = rawAngleRadians - this.previousRawAngleRadians;
        if (frameDeltaRadians > Math.PI) {
            frameDeltaRadians -= FULL_ROTATION_RADIANS;
        } else if (frameDeltaRadians < -Math.PI) {
            frameDeltaRadians += FULL_ROTATION_RADIANS;
        }
        this.accumulatedAngleRadians += frameDeltaRadians;
        this.previousRawAngleRadians = rawAngleRadians;
        this.latestAngleDegrees = this.dragStartAngleDegrees
            + this.accumulatedAngleRadians * RADIANS_TO_DEGREES;
        this.emitDragEvent('change', this.latestAngleDegrees);
        this.consumeEvent(event);
    };

    /**
     * 在窗口级释放或失焦时结束拖拽，保证画布外松开也能恢复状态。
     *
     * @param event 原始 DOM 事件。
     * @returns 无返回值。
     */
    private readonly handleWindowDragEnd = (event: Event): void => {
        if (this.draggingSectionIndex < 0) {
            return;
        }
        this.finishDrag(true);
        this.shouldSuppressNextClick = event.type === 'mouseup' || event.type === 'pointerup';
        this.consumeEvent(event);
    };

    /**
     * 消费由本次手柄按下产生的 click，防止点击穿透到下方实体面。
     *
     * @param event 鼠标点击事件。
     * @returns 无返回值。
     */
    private readonly handleClick = (event: MouseEvent): void => {
        if (!this.shouldSuppressNextClick) {
            return;
        }
        this.shouldSuppressNextClick = false;
        this.consumeEvent(event);
    };

    /**
     * 完成当前拖拽并恢复视觉状态。
     *
     * @param emitEnd 是否发布 end 事件。
     * @returns 无返回值。
     */
    private finishDrag(emitEnd: boolean): void {
        if (this.draggingSectionIndex < 0) {
            return;
        }
        if (emitEnd) {
            this.emitDragEvent('end', this.latestAngleDegrees);
        }
        this.draggingSectionIndex = -1;
        // 可视 gizmo 已在 change 中同步，结束阶段只收口交互状态，避免重复求矩阵造成释放跳变。
        this.setCursor(this.hoveredHandleIndex >= 0 ? ViewportCursorStyle.Grab : null);
        this.host.requestRender();
    }

    /**
     * 解析当前鼠标是否命中任一可见半环或箭头。
     *
     * @param event 鼠标事件。
     * @returns 命中时返回 true，并写入复用的命中槽位、轴与世界点。
     */
    private resolveHandleHit(event: MouseEvent): boolean {
        if (this.visibleSlotRoots.length === 0) {
            return false;
        }
        if (!this.updateRayFromEvent(event)) {
            return false;
        }
        this.intersectionBuffer.length = 0;
        this.raycaster.intersectObjects(this.visibleSlotRoots, true, this.intersectionBuffer);
        for (let index = 0; index < this.intersectionBuffer.length; index += 1) {
            const intersection = this.intersectionBuffer[index];
            const sectionIndex = this.objectSectionIndex.get(intersection.object);
            const axis = this.objectRotationAxis.get(intersection.object);
            if (sectionIndex === undefined || axis === undefined) {
                continue;
            }
            // Three.js Raycaster 不会依据父级 visible 自动过滤命中；确认、取消或退出后
            // 槽位虽已隐藏，也必须在业务拾取层明确排除，避免吞掉普通实体选择事件。
            if (!this.slotRoots[sectionIndex].visible) {
                continue;
            }
            this.resolvedSectionIndex = sectionIndex;
            this.resolvedRotationAxis = axis;
            this.resolvedHitPoint.copy(intersection.point);
            return true;
        }
        return false;
    }

    /**
     * 根据容器矩形更新复用射线。
     *
     * @param event 鼠标事件。
     * @returns 相机与有效容器矩形均存在时返回 true。
     */
    private updateRayFromEvent(event: MouseEvent): boolean {
        const camera = this.host.getCamera();
        const container = this.boundContainer;
        if (!camera || !container) {
            return false;
        }
        const rect = container.getBoundingClientRect();
        if (!(rect.width > 0) || !(rect.height > 0)) {
            return false;
        }
        this.pointerNdc.set(
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -((event.clientY - rect.top) / rect.height) * 2 + 1,
        );
        this.raycaster.setFromCamera(this.pointerNdc, camera);
        return true;
    }

    /**
     * 更新单个手柄的默认、悬停或拖拽材质。
     *
     * @param handleIndex 扁平手柄索引；负数表示全部恢复默认。
     * @returns 无返回值。
     */
    private setHoveredHandle(handleIndex: number): void {
        if (this.hoveredHandleIndex === handleIndex) {
            return;
        }
        if (this.hoveredHandleIndex >= 0) {
            this.ringMeshes[this.hoveredHandleIndex].material = this.defaultMaterial;
            this.headMeshes[this.hoveredHandleIndex].material = this.defaultMaterial;
        }
        this.hoveredHandleIndex = handleIndex;
        if (handleIndex >= 0) {
            this.ringMeshes[handleIndex].material = this.activeMaterial;
            this.headMeshes[handleIndex].material = this.activeMaterial;
        }
        this.host.requestRender();
    }

    /**
     * 发布复用的拖拽事件缓存。
     *
     * @param phase 生命周期阶段。
     * @param angleDegrees 连续角度。
     * @returns 无返回值。
     */
    private emitDragEvent(phase: SectionViewRotationHandleDragPhase, angleDegrees: number): void {
        const sectionIndex = this.draggingSectionIndex as SectionViewPlaneIndex;
        const axisOffset = this.draggingRotationAxis === 'x' ? 0 : DRAG_PHASES.length;
        const phaseOffset = phase === 'start' ? 0 : phase === 'change' ? 1 : 2;
        const event = this.dragEventBuffers[
            sectionIndex * ROTATION_AXES.length * DRAG_PHASES.length + axisOffset + phaseOffset
        ];
        event.angleDegrees = angleDegrees;
        this.rotationDragSubject.next(event);
    }

    /**
     * 绑定当前 Viewer 容器与窗口级释放事件。
     *
     * @returns 无返回值；容器尚未就绪时安全等待下一次 setHandle。
     */
    private bindContainerIfAvailable(): void {
        const container = this.host.getContainer();
        if (!container || this.boundContainer === container) {
            return;
        }
        this.unbindContainer();
        this.boundContainer = container;
        container.addEventListener('mousedown', this.handleMouseDown, true);
        container.addEventListener('mousemove', this.handleMouseMove, true);
        container.addEventListener('click', this.handleClick, true);
        window.addEventListener('mouseup', this.handleWindowDragEnd, true);
        window.addEventListener('pointerup', this.handleWindowDragEnd, true);
        window.addEventListener('pointercancel', this.handleWindowDragEnd, true);
        window.addEventListener('blur', this.handleWindowDragEnd, true);
    }

    /**
     * 对称解除当前 DOM 与窗口监听。
     *
     * @returns 无返回值。
     */
    private unbindContainer(): void {
        if (!this.boundContainer) {
            return;
        }
        this.boundContainer.removeEventListener('mousedown', this.handleMouseDown, true);
        this.boundContainer.removeEventListener('mousemove', this.handleMouseMove, true);
        this.boundContainer.removeEventListener('click', this.handleClick, true);
        window.removeEventListener('mouseup', this.handleWindowDragEnd, true);
        window.removeEventListener('pointerup', this.handleWindowDragEnd, true);
        window.removeEventListener('pointercancel', this.handleWindowDragEnd, true);
        window.removeEventListener('blur', this.handleWindowDragEnd, true);
        this.setCursor(null);
        /** 监听已经解除，不允许上一次取消的拖拽把 click 抑制带入下一次剖面编辑会话。 */
        this.shouldSuppressNextClick = false;
        this.boundContainer = null;
    }

    /**
     * 通过 Viewer 统一光标栈设置或恢复旋转手柄光标。
     *
     * @param cursor 手柄光标；null 表示弹出当前手柄光标并恢复栈内上一状态。
     * @returns 无返回值。
     */
    private setCursor(cursor: ViewportCursorStyle | null): void {
        if (this.currentCursor === cursor) {
            return;
        }
        if (this.currentCursor === null && cursor !== null) {
            this.host.cursorManager.push(cursor);
        } else if (this.currentCursor !== null && cursor === null) {
            this.host.cursorManager.pop(this.currentCursor);
        } else if (this.currentCursor !== null && cursor !== null) {
            this.host.cursorManager.replace(this.currentCursor, cursor);
        }
        this.currentCursor = cursor;
    }

    /**
     * 在捕获阶段终止本次手柄事件的其余选择和相机监听器。
     *
     * @param event 原始 DOM 事件。
     * @returns 无返回值。
     */
    private consumeEvent(event: Event): void {
        if (event.cancelable) {
            event.preventDefault();
        }
        event.stopImmediatePropagation();
    }

    /**
     * 把槽位与轴转换为固定数组索引。
     *
     * @param sectionIndex 零基剖面槽位。
     * @param axis 局部旋转轴。
     * @returns 0 至 5 的扁平索引。
     */
    private resolveHandleIndex(sectionIndex: SectionViewPlaneIndex, axis: SectionViewRotationAxis): number {
        return sectionIndex * ROTATION_AXES.length + (axis === 'x' ? 0 : 1);
    }

    /**
     * 校验外部槽位值。
     *
     * @param sectionIndex 外部传入槽位。
     * @returns 0、1 或 2 的精确槽位。
     */
    private requireSectionIndex(sectionIndex: number): SectionViewPlaneIndex {
        if (!Number.isInteger(sectionIndex) || sectionIndex < 0 || sectionIndex > 2) {
            throw new Error(`剖面旋转手柄槽位必须是 0、1 或 2: ${String(sectionIndex)}`);
        }
        return sectionIndex as SectionViewPlaneIndex;
    }

    /**
     * 断言管理器尚未结束生命周期。
     *
     * @returns 无返回值。
     */
    private assertNotDisposed(): void {
        if (this.isDisposed) {
            throw new Error('剖面旋转手柄管理器已释放');
        }
    }
}
